import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Calendar, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const InteriorDetailing = () => {
  return (
    <div className="min-h-screen bg-[#111216]">
      <SEO 
        title="Interior Car Detailing Simi Valley | Detailing Disciples"
        description="Premium interior detailing with deep vacuuming, steam, shampoo and surface-safe care for cars, trucks and SUVs in Simi Valley."
        canonical="https://thedetailingdisciples.com/interior-detailing"
      />

      <section className="pt-32 pb-16 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              Interior Detailing in <span className="text-[#B8860B]">Simi Valley</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              A thorough interior reset for vehicles that need more than maintenance. Services include deep vacuuming, steam treatment, carpet and upholstery shampooing, leather-safe cleaning and conditioning, crevice cleaning, and odor treatment when selected.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
              What's <span className="text-[#B8860B]">Included</span>
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <p className="text-gray-300 mb-6">The scope is matched to the condition of the interior. Services may include:</p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Detailed vacuuming of carpets, seats, crevices, and trunk</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Surface cleaning and conditioning</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Steam treatment for tough stains and sanitization</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Carpet and upholstery shampooing (when needed)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Leather-safe cleaning and conditioning</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Crevice and detail brush work</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Pet hair removal</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Odor treatment (when selected)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
              Best <span className="text-[#B8860B]">For</span>
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Vehicles with heavy use, kids, pets, or long commutes</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Pre-sale preparation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Neglected interiors that need a full reset</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Staining, odors, or embedded dirt</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-[#B8860B] to-[#C8960B]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#111216] mb-4">
            Request an Interior Detailing Quote
          </h2>
          <p className="text-[#111216]/80 text-lg mb-8 max-w-2xl mx-auto">
            Share photos of your interior condition and describe any specific concerns. We'll recommend the right level of service.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#111216] hover:bg-[#1A1A1F] text-white font-bold">
              <Calendar className="w-5 h-5 mr-2" />
              Get Your Interior Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default InteriorDetailing;